
import { useState, useEffect } from 'react';
import { DevelopmentRecipe, DevelopmentStep, DevelopmentSession } from '@/types/develop';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/hooks/use-toast';

const LOCAL_STORAGE_RECIPES_KEY = 'safelight_development_recipes';
const LOCAL_STORAGE_SESSIONS_KEY = 'safelight_development_sessions';

// Default recipes to get users started
const defaultRecipes: DevelopmentRecipe[] = [
  {
    id: 'default-hp5-d76',
    name: 'HP5+ in D-76',
    filmStock: 'Ilford HP5+',
    iso: 400,
    developer: 'Kodak D-76',
    temperature: 20,
    isUserCreated: false,
    pushPull: 'standard',
    pushPullStops: 0,
    notes: 'Standard development for HP5+ in D-76 stock solution. Initial 30s continuous agitation for developer and fixer steps, then intermittent agitation every 60s.',
    steps: [
      {
        id: uuidv4(),
        name: 'Pre-Soak',
        time: 60, // 1 minute
        agitationType: 'none',
      },
      {
        id: uuidv4(),
        name: 'Developer',
        time: 600, // 10 minutes
        agitationType: 'intermittent',
        agitationInterval: 60, // 1 minute
        agitationDuration: 10, // 10 seconds
        notes: 'Continuous agitation for first 30s, then agitate every 60s for 10s'
      },
      {
        id: uuidv4(),
        name: 'Stop Bath',
        time: 60, // 1 minute
        agitationType: 'continuous',
        agitationDuration: 5,
      },
      {
        id: uuidv4(),
        name: 'Fixer',
        time: 300, // 5 minutes
        agitationType: 'intermittent',
        agitationInterval: 60, // 1 minute
        agitationDuration: 10, // 10 seconds
        notes: 'Continuous agitation for first 30s, then agitate every 60s for 10s'
      },
      {
        id: uuidv4(),
        name: 'Fixer Remover',
        time: 120, // 2 minutes
        agitationType: 'continuous',
        agitationDuration: 120, // entire time
      },
      {
        id: uuidv4(),
        name: 'Wash',
        time: 600, // 10 minutes
        agitationType: 'intermittent',
        agitationInterval: 180, // 3 minutes
        agitationDuration: 30, // 30 seconds
      },
      {
        id: uuidv4(),
        name: 'Photo Flo',
        time: 60, // 1 minute
        agitationType: 'continuous',
        agitationDuration: 60, // entire time
      }
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'default-trix-hc110b',
    name: 'Tri-X in HC-110 (B)',
    filmStock: 'Kodak Tri-X',
    iso: 400,
    developer: 'Kodak HC-110 (Dilution B)',
    temperature: 20,
    isUserCreated: false,
    pushPull: 'standard',
    pushPullStops: 0,
    notes: 'Classic combination for Tri-X in HC-110 Dilution B. Initial 30s continuous agitation for developer and fixer steps, then intermittent agitation.',
    steps: [
      {
        id: uuidv4(),
        name: 'Pre-Soak',
        time: 60, // 1 minute
        agitationType: 'none',
      },
      {
        id: uuidv4(),
        name: 'Developer',
        time: 390, // 6.5 minutes
        agitationType: 'intermittent',
        agitationInterval: 60, // 1 minute
        agitationDuration: 5, // 5 seconds
        notes: 'Continuous agitation for first 30s, then agitate every 60s for 5s'
      },
      {
        id: uuidv4(),
        name: 'Stop Bath',
        time: 60, // 1 minute
        agitationType: 'continuous',
        agitationDuration: 5,
      },
      {
        id: uuidv4(),
        name: 'Fixer',
        time: 300, // 5 minutes
        agitationType: 'intermittent',
        agitationInterval: 60, // 1 minute
        agitationDuration: 10, // 10 seconds
        notes: 'Continuous agitation for first 30s, then agitate every 60s for 10s'
      },
      {
        id: uuidv4(),
        name: 'Fixer Remover',
        time: 120, // 2 minutes
        agitationType: 'continuous',
        agitationDuration: 120, // entire time
      },
      {
        id: uuidv4(),
        name: 'Wash',
        time: 600, // 10 minutes
        agitationType: 'intermittent',
        agitationInterval: 180, // 3 minutes
        agitationDuration: 30, // 30 seconds
      }
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
];

export const useDevelopRecipes = () => {
  const [recipes, setRecipes] = useState<DevelopmentRecipe[]>([]);
  const [sessions, setSessions] = useState<DevelopmentSession[]>([]);
  const [loading, setLoading] = useState(true);

  // Load recipes from localStorage on mount
  useEffect(() => {
    const loadRecipes = () => {
      try {
        const storedRecipes = localStorage.getItem(LOCAL_STORAGE_RECIPES_KEY);
        const storedSessions = localStorage.getItem(LOCAL_STORAGE_SESSIONS_KEY);
        
        let parsedRecipes: DevelopmentRecipe[] = [];
        if (storedRecipes) {
          parsedRecipes = JSON.parse(storedRecipes);
          
          // Check if default recipes exist in the loaded recipes
          // If not, add them back
          const hasDefaultHP5 = parsedRecipes.some(recipe => recipe.id === 'default-hp5-d76');
          const hasDefaultTriX = parsedRecipes.some(recipe => recipe.id === 'default-trix-hc110b');
          
          let defaultsAdded = false;
          
          if (!hasDefaultHP5) {
            parsedRecipes.push(defaultRecipes[0]);
            defaultsAdded = true;
          }
          
          if (!hasDefaultTriX) {
            parsedRecipes.push(defaultRecipes[1]);
            defaultsAdded = true;
          }
          
          // If we had to add back defaults, show a toast notification
          if (defaultsAdded) {
            setTimeout(() => {
              toast({
                title: "Default recipes restored",
                description: "We've restored the default development recipes that were missing.",
              });
            }, 500);
          }
        } else {
          // If no recipes found, use the default recipes
          parsedRecipes = defaultRecipes;
          localStorage.setItem(LOCAL_STORAGE_RECIPES_KEY, JSON.stringify(defaultRecipes));
        }
        
        let parsedSessions: DevelopmentSession[] = [];
        if (storedSessions) {
          parsedSessions = JSON.parse(storedSessions);
        }
        
        setRecipes(parsedRecipes);
        setSessions(parsedSessions);
      } catch (error) {
        console.error('Failed to load recipes:', error);
        // Always fall back to default recipes on error
        setRecipes(defaultRecipes);
        toast({
          title: "Error loading recipes",
          description: "We encountered an error loading your recipes. Default recipes have been restored.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadRecipes();
  }, []);

  // Save recipes to localStorage whenever they change
  useEffect(() => {
    if (!loading) {
      localStorage.setItem(LOCAL_STORAGE_RECIPES_KEY, JSON.stringify(recipes));
    }
  }, [recipes, loading]);

  // Save sessions to localStorage whenever they change
  useEffect(() => {
    if (!loading) {
      localStorage.setItem(LOCAL_STORAGE_SESSIONS_KEY, JSON.stringify(sessions));
    }
  }, [sessions, loading]);

  // Create a new recipe
  const createRecipe = (recipe: Omit<DevelopmentRecipe, 'id' | 'isUserCreated' | 'createdAt' | 'updatedAt'>) => {
    const newRecipe: DevelopmentRecipe = {
      ...recipe,
      id: uuidv4(),
      isUserCreated: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    setRecipes(prevRecipes => [...prevRecipes, newRecipe]);
    return newRecipe;
  };

  // Update an existing recipe
  const updateRecipe = (id: string, updates: Partial<Omit<DevelopmentRecipe, 'id' | 'createdAt' | 'updatedAt'>>) => {
    setRecipes(prevRecipes => 
      prevRecipes.map(recipe => 
        recipe.id === id 
          ? { 
              ...recipe, 
              ...updates, 
              updatedAt: new Date().toISOString() 
            } 
          : recipe
      )
    );
  };

  // Delete a recipe
  const deleteRecipe = (id: string) => {
    // Don't allow deletion of default recipes
    const recipeToDelete = recipes.find(recipe => recipe.id === id);
    if (recipeToDelete && !recipeToDelete.isUserCreated) {
      toast({
        title: "Cannot delete default recipe",
        description: "Default recipes cannot be deleted. You can create a copy and modify it instead.",
        variant: "destructive",
      });
      return;
    }
    
    setRecipes(prevRecipes => prevRecipes.filter(recipe => recipe.id !== id));
  };

  // Calculate time adjustment for push/pull processing
  const calculateTimeAdjustment = (baseTime: number, pushPull: 'standard' | 'push' | 'pull', stops: number) => {
    if (pushPull === 'standard' || stops === 0) {
      return baseTime;
    }
    
    if (pushPull === 'push') {
      // Typically, pushing one stop increases development time by ~30%
      return baseTime * (1 + (stops * 0.3));
    } else {
      // Pulling one stop decreases development time by ~15%
      return baseTime * (1 - (stops * 0.15));
    }
  };

  // Add a development session
  const addSession = (session: Omit<DevelopmentSession, 'id'>) => {
    const newSession: DevelopmentSession = {
      ...session,
      id: uuidv4()
    };
    
    setSessions(prevSessions => [...prevSessions, newSession]);
    return newSession;
  };

  // Update a session
  const updateSession = (id: string, updates: Partial<Omit<DevelopmentSession, 'id'>>) => {
    setSessions(prevSessions => 
      prevSessions.map(session => 
        session.id === id 
          ? { ...session, ...updates } 
          : session
      )
    );
  };

  // Delete a session
  const deleteSession = (id: string) => {
    setSessions(prevSessions => prevSessions.filter(session => session.id !== id));
  };

  return {
    recipes,
    sessions,
    loading,
    createRecipe,
    updateRecipe,
    deleteRecipe,
    calculateTimeAdjustment,
    addSession,
    updateSession,
    deleteSession
  };
};
